import { api } from './client';
import type { Registration, FamilyMember, VillaPreference, AgeGroup, DietaryRestriction } from '../types/registration';

export interface RegistrationResponse {
  registration: Registration | null;
}

export interface SaveDraftData {
  isGroupLeader?: boolean;
  phone?: string;
  familyMembers?: FamilyMember[];
  villaPreference?: VillaPreference;
}

export interface UpdateDetailsData {
  phone?: string;
  familyConnection?: string | null;
}

export interface UpdateMembersData {
  familyMembers: Array<{
    id?: string;
    firstName: string;
    lastName: string;
    ageGroup: AgeGroup;
    relationship: string;
    email?: string | null;
    dietaryRestrictions?: DietaryRestriction[];
    foodAllergies?: string | null;
    accessibilityNeeds?: string | null;
    sortOrder?: number;
  }>;
}

export interface UpdateVillaPreferenceData {
  villaTypePreference: string | null;
  flexibilityNotes: string | null;
  roomConfigNotes: string | null;
}

export interface RegistrationEditor {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  createdAt: string;
}

export const registrationsApi = {
  /** GET /api/registrations — fetch current user's registration for active reunion */
  getMine: () =>
    api.get<RegistrationResponse>('/registrations'),

  /** POST /api/registrations — create or update draft */
  saveDraft: (data: SaveDraftData) =>
    api.post<RegistrationResponse>('/registrations', data),

  /** PUT /api/registrations/:id/submit — submit registration */
  submit: (registrationId: string) =>
    api.put<RegistrationResponse>(`/registrations/${registrationId}/submit`),

  /** PUT /api/registrations/:id/details */
  updateDetails: (id: string, data: UpdateDetailsData) =>
    api.put<{ data: Registration }>(`/registrations/${id}/details`, data),

  /** PUT /api/registrations/:id/members */
  updateMembers: (id: string, data: UpdateMembersData) =>
    api.put<{ data: FamilyMember[] }>(`/registrations/${id}/members`, data),

  /** PUT /api/registrations/:id/villa-preference */
  updateVillaPreference: (id: string, data: UpdateVillaPreferenceData) =>
    api.put<{ data: VillaPreference }>(`/registrations/${id}/villa-preference`, data),

  /** GET /api/registrations/:id/editors */
  getEditors: (id: string) =>
    api.get<{ data: RegistrationEditor[] }>(`/registrations/${id}/editors`),

  /** PUT /api/registrations/:id/editors — add editor */
  addEditor: (id: string, userId: string) =>
    api.put<{ data: RegistrationEditor }>(`/registrations/${id}/editors`, { userId }),

  /** DELETE /api/registrations/:id/editors — remove editor */
  removeEditor: (id: string, userId: string) =>
    api.delete<{ data: { removed: boolean } }>(`/registrations/${id}/editors`, { userId }),
};
